import type { LogCategoryItem } from '@lmc/ts-common'
import { Chevron, MultiSelect } from '@lmc/ui-kit'
import { useGroupedCarers } from 'hooks/useGroupedOptions'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { DashboardFilters } from 'screens/Reports/MonitoringReports'
import type { Filter } from 'screens/Reports/MonitoringReports/components/DashboardFilters/SelectedFilters'
import type { FilterValues } from 'screens/Reports/MonitoringReports/components/MonitoringReport/MonitoringReport'
import styled from 'styled-components'
import { getStatusOptions } from './utils'

type IncidentActionsFilterValues = FilterValues & {
  statuses: string[]
  ownedBy: string[]
  currentPage?: number
  itemsPerPage?: number
}

interface ActionsFiltersProps {
  filterParams: IncidentActionsFilterValues
  onFilterChange: (value: Partial<IncidentActionsFilterValues>) => void
  testId?: string
}

const INCIDENT_CATEGORY_ITEM = '5e867702b349f776c8d48c34'

const ActionsFilters: React.FC<ActionsFiltersProps> = ({
  filterParams,
  onFilterChange,
  testId = 'actions-filters'
}) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { groupedCarerOptions } = useGroupedCarers()

  const statusOptions = getStatusOptions(t)
  const {
    'log-category-items': categoryItems
  }: {
    'log-category-items': LogCategoryItem[] | undefined
  } = useSelector(
    (state: { data: { 'log-category-items': LogCategoryItem[] } }) => state.data
  )

  const mappedSelectedFilters = {
    ...(filterParams.rangePicker && { rangePicker: filterParams.rangePicker }),
    ...(filterParams.residents &&
      filterParams.residents.length > 0 && {
        residents: filterParams.residents
      }),
    ...(filterParams.categoryItems &&
      filterParams.categoryItems.length > 0 && {
        categoryItems: filterParams.categoryItems.map(
          (category) =>
            categoryItems?.find((c) => c.id.toString() === category)?.name
        )
      }),
    ...(filterParams.statuses &&
      filterParams.statuses.length > 0 && {
        statuses: filterParams.statuses.map(
          (status) =>
            statusOptions.find((option) => option.value === status)?.label ||
            status
        )
      }),
    ...(filterParams.ownedBy &&
      filterParams.ownedBy.length > 0 && {
        ownedBy: filterParams.ownedBy.map((carerId) => {
          const allCarerOptions = groupedCarerOptions.flatMap(
            (group) => group.options
          )
          return (
            allCarerOptions.find(
              (option) => String(option.value) === String(carerId)
            )?.label || carerId
          )
        })
      })
  }

  return (
    <DashboardFilters
      data-testid={testId}
      onChange={onFilterChange}
      value={filterParams}
      withLoggedBy={false}
      reportingId="items"
      itemsPlaceholder={t(incidentReportsConstants.INCIDENT_TITLE)}
      itemsCategoryFilter={INCIDENT_CATEGORY_ITEM}
      hasResidentsFilter={false}
      allowClearDateRange={false}
      variant="highContrast"
      selectedFilters={mappedSelectedFilters as Filter}
    >
      <StyledMultiSelect
        data-testid={`${testId}-status-select`}
        title={t(incidentReportsConstants.STATUS)}
        options={statusOptions}
        values={filterParams.statuses}
        onChange={(selectedStatus) =>
          onFilterChange({ statuses: selectedStatus as string[] })
        }
        variant="highContrast"
        suffixIcon={<Chevron direction="down" />}
      />
      <StyledMultiSelect
        data-testid={`${testId}-owned-by-select`}
        title={t(incidentReportsConstants.OWNED_BY)}
        values={filterParams.ownedBy}
        onChange={(selectedCarers) =>
          onFilterChange({ ownedBy: selectedCarers.map(String) })
        }
        groupedOptions={groupedCarerOptions}
        variant="highContrast"
        suffixIcon={<Chevron direction="down" />}
      />
    </DashboardFilters>
  )
}

const StyledMultiSelect = styled(MultiSelect)`
  min-width: 200px;

  .ant-select-selector {
    width: 200px;
  }

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    min-width: unset;

    .ant-select-selector {
      width: auto;
    }
  }
`

export default ActionsFilters
export type { IncidentActionsFilterValues }
